/**
 * SportMemoryDetailPage — /patrimoine/:slug (V12.x)
 *
 * Détail d'une fiche patrimoniale : badge contenu, CTA adaptatif selon `media_type`,
 * crédits source obligatoires, lien externe cible=_blank.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, ExternalLink, Video, BookOpen, FileText, FolderArchive,
  ArrowRight, Users as UsersIcon, Calendar, MapPin, Trophy, Newspaper, Landmark, ScrollText, Archive as ArchiveIcon, Flame, Info,
} from "lucide-react";

const BADGE = {
  ARCHIVE_VIDEO: { label: "ARCHIVE VIDÉO", icon: Video, color: "bg-red-100 text-red-800 border-red-200" },
  ARCHIVE_AUDIO: { label: "ARCHIVE AUDIO", icon: Video, color: "bg-red-100 text-red-800 border-red-200" },
  ARCHIVE_PHOTO: { label: "ARCHIVE PHOTO", icon: ArchiveIcon, color: "bg-orange-100 text-orange-800 border-orange-200" },
  ARTICLE_HISTORIQUE: { label: "ARTICLE HISTORIQUE", icon: Newspaper, color: "bg-blue-100 text-blue-800 border-blue-200" },
  LIVRE: { label: "OUVRAGE", icon: BookOpen, color: "bg-amber-100 text-amber-900 border-amber-200" },
  THESE: { label: "THÈSE", icon: ScrollText, color: "bg-purple-100 text-purple-800 border-purple-200" },
  ETUDE: { label: "ÉTUDE", icon: ScrollText, color: "bg-purple-100 text-purple-800 border-purple-200" },
  RESULTATS: { label: "RÉSULTATS", icon: Trophy, color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  PALMARES: { label: "PALMARÈS", icon: Trophy, color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  TEMOIGNAGE: { label: "TÉMOIGNAGE", icon: UsersIcon, color: "bg-cyan-100 text-cyan-800 border-cyan-200" },
  EVENEMENT: { label: "ÉVÉNEMENT", icon: Flame, color: "bg-rose-100 text-rose-800 border-rose-200" },
  PORTRAIT: { label: "PORTRAIT", icon: UsersIcon, color: "bg-cyan-100 text-cyan-800 border-cyan-200" },
  DOCUMENT_OFFICIEL: { label: "DOCUMENT OFFICIEL", icon: FileText, color: "bg-slate-100 text-slate-800 border-slate-200" },
  CATALOGUE_ARCHIVES: { label: "CATALOGUE D'ARCHIVES", icon: FolderArchive, color: "bg-slate-100 text-slate-800 border-slate-200" },
  HISTOIRE_INSTITUTIONNELLE: { label: "SOURCE INSTITUTIONNELLE", icon: Landmark, color: "bg-indigo-100 text-indigo-800 border-indigo-200" },
};

const CTA = {
  VIDEO: { label: "Voir la vidéo", icon: Video },
  PDF: { label: "Consulter le document", icon: FileText },
  BOOK: { label: "Voir la référence", icon: BookOpen },
  WEBPAGE: { label: "Lire la source", icon: ExternalLink },
  ARCHIVE_CATALOG: { label: "Explorer les archives", icon: FolderArchive },
  NONE: { label: "Voir la fiche", icon: ArrowRight },
};

export default function SportMemoryDetailPage() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/sport-memory/${slug}`)
      .then((r) => setItem(r.data))
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="mx-auto max-w-4xl p-8"><Card className="p-6 text-center text-sm text-muted-foreground">Chargement…</Card></div>;
  if (notFound || !item) {
    return (
      <div className="mx-auto max-w-3xl p-8 text-center">
        <p className="text-muted-foreground">Fiche introuvable.</p>
        <Link to="/memoire" className="text-[hsl(var(--aito-lagoon))] hover:underline text-sm">← Retour à la Mémoire</Link>
      </div>
    );
  }

  const badge = BADGE[item.content_type] || BADGE.ARTICLE_HISTORIQUE;
  const cta = CTA[item.media_type] || CTA.NONE;
  const primaryUrl = item.media_url || item.document_url || item.source_url;
  const BadgeIcon = badge.icon;
  const CtaIcon = cta.icon;
  const yearLabel = item.year_start
    ? (item.year_end && item.year_end !== item.year_start ? `${item.year_start}–${item.year_end}` : String(item.year_start))
    : null;

  return (
    <article className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8" data-testid="sport-memory-detail">
      <Link to="/memoire" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft className="h-3 w-3" /> Retour à la Mémoire
      </Link>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <Badge variant="outline" className={`text-[11px] font-semibold ${badge.color}`}>
          <BadgeIcon className="h-3 w-3 mr-1" /> {badge.label}
        </Badge>
        {yearLabel && <Badge variant="outline" className="text-[11px] font-mono">{yearLabel}</Badge>}
        {item.sport && <Badge variant="outline" className="text-[11px] capitalize">{item.sport}</Badge>}
        {item.event && <Badge variant="outline" className="text-[11px]">{item.event}</Badge>}
        {item.duration && <Badge variant="outline" className="text-[11px] text-muted-foreground">{item.duration}</Badge>}
        {item.link_status === "to_review" && (
          <Badge variant="outline" className="text-[11px] bg-yellow-50 text-yellow-800 border-yellow-200">Lien à vérifier</Badge>
        )}
      </div>
      <h1 className="font-display font-bold text-3xl sm:text-4xl leading-tight">{item.title}</h1>
      {item.subtitle && <p className="text-base sm:text-lg text-muted-foreground italic mt-2">{item.subtitle}</p>}
      {item.description_short && (
        <p className="mt-5 text-base leading-relaxed">{item.description_short}</p>
      )}
      {item.description_long && (
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{item.description_long}</p>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        {primaryUrl ? (
          <Button asChild size="lg" className="bg-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon))]/90 text-white" data-testid="sport-memory-primary-cta">
            <a href={primaryUrl} target="_blank" rel="noopener noreferrer">
              <CtaIcon className="h-4 w-4 mr-2" /> {cta.label}
              <ExternalLink className="h-3.5 w-3.5 ml-2 opacity-70" />
            </a>
          </Button>
        ) : null}
        {item.source_url && item.source_url !== primaryUrl && (
          <Button asChild variant="outline" size="lg">
            <a href={item.source_url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-2" /> Voir la source
            </a>
          </Button>
        )}
      </div>

      {/* Métadonnées */}
      <Card className="p-4 mt-8 text-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {item.author && <div><span className="text-muted-foreground">Auteur :</span> <strong>{item.author}</strong></div>}
          {item.publisher && <div><span className="text-muted-foreground">Éditeur :</span> <strong>{item.publisher}</strong></div>}
          {item.publication_year && <div><span className="text-muted-foreground">Année de publication :</span> <strong>{item.publication_year}</strong></div>}
          {item.archive_reference && <div><span className="text-muted-foreground">Référence archive :</span> <strong className="font-mono">{item.archive_reference}</strong></div>}
          {item.event_type && <div><span className="text-muted-foreground">Type d'événement :</span> <strong>{item.event_type}</strong></div>}
          {item.archipelago && <div><span className="text-muted-foreground">Archipel :</span> <strong>{item.archipelago}</strong></div>}
          {(item.people || []).length > 0 && <div className="col-span-full"><span className="text-muted-foreground">Personnes :</span> <strong>{item.people.join(", ")}</strong></div>}
          {(item.organizations || []).length > 0 && <div className="col-span-full"><span className="text-muted-foreground">Organisations :</span> <strong>{item.organizations.join(", ")}</strong></div>}
          {(item.sports || []).length > 0 && <div className="col-span-full"><span className="text-muted-foreground">Disciplines :</span> <strong>{item.sports.join(", ")}</strong></div>}
          {item.bibliographic_note && <div className="col-span-full italic text-muted-foreground">{item.bibliographic_note}</div>}
        </div>
        {(item.tags || []).length > 0 && (
          <div className="mt-3 pt-3 border-t flex flex-wrap gap-1.5">
            {item.tags.map((t) => <Badge key={t} variant="secondary" className="text-[10px]">#{t}</Badge>)}
          </div>
        )}
      </Card>

      {/* Crédits obligatoires */}
      <Card className="p-4 mt-4 bg-secondary/30 border-dashed text-xs text-muted-foreground" data-testid="sport-memory-credits">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            {item.source_name && <div><span className="font-semibold text-foreground">Source :</span> {item.source_name}{item.source_type ? ` · ${item.source_type}` : ""}</div>}
            <div className="mt-1">Ressource externe référencée par AITO.PF à des fins documentaires et patrimoniales. Le contenu reste hébergé et crédité par ses ayants droit.</div>
            {item.image_credit && <div className="mt-1"><span className="font-semibold text-foreground">Crédit image :</span> {item.image_credit}</div>}
          </div>
        </div>
      </Card>
    </article>
  );
}
